import { Link } from "wouter";
import { SEOHead, ORGANIZATION_SCHEMA } from "@/components/seo-head";
import { Button } from "@/components/ui/button";
import { SeoPageHeader } from "@/components/seo-page-header";
import { ChevronRight, CheckCircle2, XCircle, Minus, ArrowRight } from "lucide-react";

const PAGE_URL = "https://thepropertool.com/best/quoting-software-for-uk-builders";

const WEBPAGE_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "WebPage",
  "name": "Best Quoting Software for UK Builders: Full Comparison (2026)",
  "description": "A genuinely neutral comparison of quoting and estimating software for UK builders: The Proper Tool, Tradify, Buildxact, simPRO, EstimatorXpress, PriceAJob, esti-mate, and Spreadsheets.",
  "url": PAGE_URL,
  "dateModified": "2026-09-19",
  "publisher": {
    "@type": "Organization",
    "name": "The Proper Tool",
    "url": "https://thepropertool.com"
  }
};

const BREADCRUMB_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": [
    { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://thepropertool.com/" },
    { "@type": "ListItem", "position": 2, "name": "Compare", "item": "https://thepropertool.com/comparisons" },
    { "@type": "ListItem", "position": 3, "name": "Best Quoting Software for UK Builders", "item": PAGE_URL },
  ],
};

const FAQ_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": [
    {
      "@type": "Question",
      "name": "What quoting software do UK builders use?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "UK builders use a range of tools depending on their business size and work type. Sole traders and small trade businesses may use Tradify or spreadsheets, while Price A Job and EstimatorXpress support more structured estimating. Simpro is aimed at field-service teams and complex operations. Buildxact has UK plans and supports digital takeoffs for residential work. The right choice depends on whether your priority is quoting speed, estimating depth, job management, or full project lifecycle management.",
      },
    },
    {
      "@type": "Question",
      "name": "Is there a Buildxact alternative built for the UK market?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Yes. Buildxact has UK plans and site content, with monthly list pricing from £119 for Foundation, £209 for Pro, and £349 for Master, or annual-equivalent pricing from £89, £160, and £262. It offers digital takeoffs and supplier catalogues, while The Proper Tool focuses on UK residential quoting with a 31-category cost library, AI scope generation, and structural drawing analysis.",
      },
    },
    {
      "@type": "Question",
      "name": "Is EstimatorXpress still available as a cloud tool?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "EstimatorXpress is a Windows-oriented estimating tool with annual subscriptions, perpetual licences, and a free trial whose duration is not published. Its Pro+Take-Off edition supports plan takeoff, but this remains partly manual rather than autonomous AI drawing understanding. It remains relevant for builders who want established UK material resources and detailed traditional takeoffs.",
      },
    },
    {
      "@type": "Question",
      "name": "What is the best simPRO alternative for small builders?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "For small builders whose main need is quoting, Simpro may provide more operational software than the job requires. It is aimed at field-service teams and complex scheduling, inventory, and reporting workflows. Tradify covers quoting, scheduling, and invoicing for smaller trade businesses, while The Proper Tool focuses on construction quoting from drawings or descriptions without the overhead of a full field-service platform.",
      },
    },
    {
      "@type": "Question",
      "name": "Do I need job management software or just quoting software?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "If your main challenge is spending too long pricing jobs, missing items that cause under-pricing, or losing work because quotes are too slow, you need quoting software. If your challenge is scheduling jobs, coordinating a team, tracking job progress, and managing invoicing once work starts, you need job management software. Many builders find they are better served by a dedicated quoting tool for estimating and a separate tool (or none) for job management. Some all-in-one tools like Tradify cover both at a basic level.",
      },
    },
  ],
};

interface ToolRow {
  name: string;
  altHref?: string;
  aiQuoting: "yes" | "partial" | "no";
  ukCostLibrary: "yes" | "partial" | "no";
  readsDrawings: "yes" | "partial" | "no";
  mobile: "yes" | "partial" | "no";
  pricePerMonth: string;
  freeTrial: string;
  bestFor: string;
}

const TOOLS: ToolRow[] = [
  {
    name: "The Proper Tool",
    aiQuoting: "yes",
    ukCostLibrary: "yes",
    readsDrawings: "yes",
    mobile: "yes",
    pricePerMonth: "From £29/mo",
    freeTrial: "14 days",
    bestFor: "UK extensions, loft conversions & structural alterations: AI-powered, reads drawings",
  },
  {
    name: "Tradify",
    altHref: "/tradify-alternative",
    aiQuoting: "no",
    ukCostLibrary: "partial",
    readsDrawings: "no",
    mobile: "yes",
    pricePerMonth: "From £34/user/mo",
    freeTrial: "14 days",
    bestFor: "Small trade businesses needing quoting + scheduling + invoicing in one tool",
  },
  {
    name: "Buildxact",
    altHref: "/buildxact-alternative",
    aiQuoting: "partial",
    ukCostLibrary: "partial",
    readsDrawings: "partial",
    mobile: "partial",
    pricePerMonth: "From £89/mo (annual billing)",
    freeTrial: "14 days",
    bestFor: "UK residential work needing digital takeoffs and job costing",
  },
  {
    name: "simPRO",
    altHref: "/simpro-alternative",
    aiQuoting: "no",
    ukCostLibrary: "partial",
    readsDrawings: "partial",
    mobile: "yes",
    pricePerMonth: "Request pricing",
    freeTrial: "Guided demo",
    bestFor: "Larger field service teams (10+) with complex scheduling and job management needs",
  },
  {
    name: "EstimatorXpress",
    altHref: "/estimatorxpress-alternative",
    aiQuoting: "no",
    ukCostLibrary: "yes",
    readsDrawings: "partial",
    mobile: "partial",
    pricePerMonth: "From £899/year + VAT",
    freeTrial: "Free trial (duration not published)",
    bestFor: "Traditional house builders and housing associations doing volume new-build",
  },
  {
    name: "PriceAJob",
    altHref: "/pricejob-alternative",
    aiQuoting: "no",
    ukCostLibrary: "yes",
    readsDrawings: "partial",
    mobile: "yes",
    pricePerMonth: "From £35.95/mo",
    freeTrial: "Free demo, no card",
    bestFor: "Cloud estimating with UK material prices and manual takeoff",
  },
  {
    name: "esti-mate",
    altHref: "/estimage-alternative",
    aiQuoting: "no",
    ukCostLibrary: "yes",
    readsDrawings: "partial",
    mobile: "partial",
    pricePerMonth: "From £1,515 + VAT",
    freeTrial: "20 days",
    bestFor: "Traditional Windows estimating and takeoff for UK construction",
  },
  {
    name: "Spreadsheets",
    aiQuoting: "no",
    ukCostLibrary: "no",
    readsDrawings: "no",
    mobile: "partial",
    pricePerMonth: "Free",
    freeTrial: "N/A",
    bestFor: "Very simple jobs or builders who are just starting out and have time to maintain templates",
  },
];

type TriState = "yes" | "partial" | "no";

function TriCell({ value }: { value: TriState }) {
  if (value === "yes") return <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400" />;
  if (value === "no") return <XCircle className="h-4 w-4 text-red-500 dark:text-red-400" />;
  return <Minus className="h-4 w-4 text-amber-600 dark:text-amber-400" />;
}

const TOOL_DETAILS = [
  {
    name: "The Proper Tool",
    altHref: undefined as string | undefined,
    strengths: [
      "Built exclusively for UK residential construction: extensions, loft conversions, structural alterations.",
      "AI generates a fully itemised scope from a plain description or uploaded drawings in under 10 minutes.",
      "31-category UK cost library with labour, materials, and plant breakdowns per line item.",
      "Expert validation flags scope gaps, missing dependencies, and quantity outliers before you send.",
    ],
    limitations: [
      "Focused on quoting and estimating only. No built-in invoicing, job scheduling, or CRM.",
      "Most relevant for residential builders; less suited to large commercial or civil engineering work.",
    ],
    stayIf: "Your main bottleneck is quoting speed and accuracy on UK residential work.",
  },
  {
    name: "Tradify",
    altHref: "/tradify-alternative",
    strengths: [
      "All-in-one platform: quoting, scheduling, invoicing, and customer tracking in one tool.",
      "Available on iPhone, iPad, Android, and the web, which suits teams working across devices.",
      "Good for simpler trade jobs: electrical, plumbing, HVAC, painting, and general maintenance.",
      "AI SmartWrite and SmartRead support writing and bill capture, but are not evidenced as priced quote generation from scopes or drawings.",
    ],
    limitations: [
      "Quoting module is manual: no AI scope generation, no drawing analysis.",
      "Price-list management and imports are not the same as an included universal UK construction cost book.",
      "Per-seat pricing increases cost as the team grows.",
    ],
    stayIf: "You need scheduling, invoicing, and quoting in one tool and mainly do service-trade work.",
  },
  {
    name: "Buildxact",
    altHref: "/buildxact-alternative",
    strengths: [
      "Digital takeoffs for residential projects, with Blu Estimate Generator and Blu Takeoff Assistant options.",
      "Comprehensive job cost management alongside estimating.",
      "UK plans and site content are available, with supplier catalogues and live pricing.",
      "Blu Estimate Generator is listed at +£99/month on Foundation and included on higher plans where shown; Blu Takeoff Assistant is +£99/month or included with Master where shown.",
    ],
    limitations: [
      "Supplier catalogues and live pricing are not evidence of a universal UK construction cost book.",
      "Blu Estimate Generator and Blu Takeoff Assistant are paid add-ons on some plans, rather than a uniform included feature.",
      "Setup requires significant configuration time before the first quote.",
      "Highest price tier of the tools compared here.",
    ],
    stayIf: "You need digital takeoffs and job costing, and its UK plans, supplier pricing, and add-on AI tools fit your workflow.",
  },
  {
    name: "simPRO",
    altHref: "/simpro-alternative",
    strengths: [
      "Comprehensive field service management: dispatching, scheduling, inventory, and reporting.",
      "Simpro Mobile supports teams working in the field.",
      "Handles complex multi-technician workflows that simpler tools cannot.",
      "Optional Takeoffs supports plan upload, manual measurement and markup, plus symbol counting or recognition.",
    ],
    limitations: [
      "Public pricing is not listed; the buying path is a guided personalised demo rather than a self-serve trial.",
      "Takeoffs provides partial drawing assistance, not full autonomous drawing understanding.",
      "Quoting is a module within a much larger (and more complex) platform.",
    ],
    stayIf: "You run a field-service team with complex scheduling, inventory, mobile work, or multi-technician operations.",
  },
  {
    name: "EstimatorXpress",
    altHref: "/estimatorxpress-alternative",
    strengths: [
      "6,000+ UK material resources with Price Tracker support traditional estimating.",
      "Pro+Take-Off supports plan takeoff for more detailed estimates.",
      "Annual subscriptions are £899 Essential, £1,099 Pro, and £1,299 Pro+Take-Off plus VAT; perpetual licences are £1,599, £2,199, and £2,799 plus VAT.",
    ],
    limitations: [
      "Windows-oriented with limited mobile use.",
      "Plan takeoff is partial and manual, not AI drawing understanding or scope-to-priced-quote generation.",
      "Free-trial duration is not published; perpetual plans also list £29.99/month for support and updates.",
    ],
    stayIf: "You need an established UK material database and traditional plan takeoff on Windows.",
  },
  {
    name: "PriceAJob",
    altHref: "/pricejob-alternative",
    strengths: [
      "Cloud-based and works on every device.",
      "5,000+ material prices with nightly updates.",
      "Drawing and document management, quantity and material takeoff, and blueprint import support structured estimating.",
    ],
    limitations: [
      "Takeoff and blueprint import are partial/manual rather than AI drawing reading.",
      "No evidence of AI scope-to-priced-quote generation.",
      "The free offer is a demo with no card, not a stated free trial.",
    ],
    stayIf: "You want a cloud estimator with regularly updated UK material prices and manual takeoff tools.",
  },
  {
    name: "esti-mate",
    altHref: "/estimage-alternative",
    strengths: [
      "Current esti-mate is a Windows construction estimating product with established traditional workflows.",
      "Optional BCIS Wessex libraries and On-Screen Take Off support UK pricing and assisted PDF takeoff.",
    ],
    limitations: [
      "The base licence starts at £1,515 excluding VAT and is subject to an annual licence fee; a 20-day free trial is available.",
      "Optional BCIS Wessex pricing libraries start at £410; On-Screen Take Off costs £455 and is manual/assisted.",
      "It runs on Windows platforms and most Windows networks; no native iOS/Android support or cloud access is advertised.",
    ],
    stayIf: "You want traditional Windows estimating with optional UK cost libraries and assisted takeoff.",
  },
  {
    name: "Spreadsheets",
    altHref: undefined as string | undefined,
    strengths: [
      "Free, flexible, and familiar: most people already know how to use Excel or Google Sheets.",
      "Fine for very simple, repeat jobs where you can copy a previous quote.",
    ],
    limitations: [
      "No rate database: you build and maintain your own.",
      "Formula errors cause under-pricing; no scope validation.",
      "Manual formatting for every quote; no professional PDF output without extra work.",
      "Takes hours per quote for anything complex.",
    ],
    stayIf: "You do very simple, repetitive jobs and have time to build and maintain your own templates.",
  },
];

const FAQS = [
  {
    q: "What quoting software do UK builders use?",
    a: "UK builders use a range of tools depending on their business size and work type. Sole traders and small trade businesses may use Tradify or spreadsheets, while Price A Job and EstimatorXpress support more structured estimating. Simpro is aimed at field-service teams and complex operations. Buildxact has UK plans and supports digital takeoffs for residential work. The right choice depends on whether your priority is quoting speed, estimating depth, job management, or full project lifecycle management.",
  },
  {
    q: "Is there a Buildxact alternative built for the UK market?",
    a: "Yes. Buildxact has UK plans and site content, with monthly list pricing from £119 for Foundation, £209 for Pro, and £349 for Master, or annual-equivalent pricing from £89, £160, and £262. It offers digital takeoffs and supplier catalogues, while The Proper Tool focuses on UK residential quoting with a 31-category cost library, AI scope generation, and structural drawing analysis.",
  },
  {
    q: "Is EstimatorXpress still available as a cloud tool?",
    a: "EstimatorXpress is a Windows-oriented estimating tool with annual subscriptions, perpetual licences, and a free trial whose duration is not published. Its Pro+Take-Off edition supports plan takeoff, but this remains partly manual rather than autonomous AI drawing understanding. It remains relevant for builders who want established UK material resources and detailed traditional takeoffs.",
  },
  {
    q: "What is the best simPRO alternative for small builders?",
    a: "For small builders whose main need is quoting, Simpro may provide more operational software than the job requires. It is aimed at field-service teams and complex scheduling, inventory, and reporting workflows. Tradify covers quoting, scheduling, and invoicing for smaller trade businesses, while The Proper Tool focuses on construction quoting from drawings or descriptions without the overhead of a full field-service platform.",
  },
  {
    q: "Do I need job management software or just quoting software?",
    a: "If your main challenge is spending too long pricing jobs, missing items that cause under-pricing, or losing work because quotes are too slow, you need quoting software. If your challenge is scheduling jobs, coordinating a team, tracking job progress, and managing invoicing once work starts, you need job management software. Many builders find they are better served by a dedicated quoting tool for estimating and a separate tool (or none) for job management. Some all-in-one tools like Tradify cover both at a basic level.",
  },
];

export default function BestQuotingSoftwareForUKBuildersPage() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <SEOHead
        title="Best Quoting Software for UK Builders: Full Comparison (2026)"
        description="A neutral, checked 19 September 2026 comparison of quoting software for UK builders: The Proper Tool, Tradify, Buildxact, Simpro, EstimatorXpress, Price A Job, esti-mate, and spreadsheets. Pricing, capabilities, and measured use-case verdicts."
        canonical="/best/quoting-software-for-uk-builders"
        schema={[WEBPAGE_SCHEMA, BREADCRUMB_SCHEMA, FAQ_SCHEMA, ORGANIZATION_SCHEMA]}
      />

      <SeoPageHeader />

      <main>
        {/* Hero */}
        <section className="py-16 sm:py-24 bg-gradient-to-b from-primary/5 to-background">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-6 justify-center">
              <Link href="/" className="hover:text-foreground transition-colors">Home</Link>
              <ChevronRight className="h-4 w-4" />
              <Link href="/comparisons" className="hover:text-foreground transition-colors">Compare</Link>
              <ChevronRight className="h-4 w-4" />
              <span>Best Quoting Software for UK Builders</span>
            </div>
            <span className="inline-block text-xs font-semibold px-3 py-1 rounded-full bg-primary/10 text-primary mb-4">2026 Comparison</span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight tracking-tight mb-6">
              Quoting Software for UK Builders: a Neutral Comparison
            </h1>
            <p className="text-lg sm:text-xl text-muted-foreground leading-relaxed mb-8 max-w-3xl mx-auto">
              Eight tools, honest verdicts. Here's the full landscape. Pick the right quoting software for your building business.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button size="lg" asChild>
                <Link href="#comparison-table">
                  See the comparison
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
              <Button size="lg" variant="outline" asChild>
                <Link href="/comparisons">All competitor pages</Link>
              </Button>
            </div>
            <p className="text-xs text-muted-foreground mt-6 max-w-xl mx-auto">
              We compare tools we genuinely know. Where The Proper Tool competes, we say so directly. Where a competitor is stronger for a specific use case, we say that too.
            </p>
          </div>
        </section>

        {/* What matters */}
        <section className="py-16 sm:py-20 border-t border-border/40">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <h2 className="text-3xl sm:text-4xl font-bold mb-6">What to look for in quoting software for UK builders</h2>
            <p className="text-lg text-muted-foreground mb-8 leading-relaxed">
              The UK construction market has specific requirements many generic quoting tools miss. Four things matter most:
            </p>
            <div className="space-y-5">
              {[
                { title: "UK-calibrated cost data", desc: "UK labour and materials rates are not interchangeable with overseas benchmarks. EstimatorXpress and The Proper Tool have UK pricing resources. Buildxact offers supplier catalogues and live pricing on UK plans, while Tradify supports price-list management and imports rather than an included universal UK construction cost book." },
                { title: "Scope completeness, not just line items", desc: "Under-pricing usually comes from missing items, not wrong rates. Tools that actively check whether all expected trade items are present (The Proper Tool does this) catch the gaps that erode margin before you send a quote." },
                { title: "Speed vs depth trade-off", desc: "Complex estimating tools such as Buildxact and EstimatorXpress produce detailed takeoffs but require setup and manual input. Simpro Takeoffs and Price A Job support partial or manual drawing workflows. AI-powered tools generate scopes quickly but still need review on complex structural jobs." },
                { title: "Business size and workflow fit", desc: "simPRO is built for 10-person field service teams. Tradify suits small trade businesses. EstimatorXpress suits house builders. The Proper Tool is built for residential builders doing extensions and alterations. Choose the tool sized to your business, not the most feature-rich one." },
                { title: "Total cost of ownership", desc: "Published per-month price is only part of the picture. Add setup time, training investment, and ongoing rate-database maintenance. A cheaper tool that takes 40 hours to configure may cost more than a pricier one that works in 10 minutes." },
              ].map((item) => (
                <div key={item.title} className="flex items-start gap-3">
                  <CheckCircle2 className="h-5 w-5 text-primary flex-shrink-0 mt-1" />
                  <div>
                    <span className="font-semibold">{item.title}: </span>
                    <span className="text-muted-foreground">{item.desc}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Comparison table */}
        <section className="py-16 sm:py-20 bg-muted/30 border-t border-border/40" id="comparison-table">
          <div className="max-w-6xl mx-auto px-4 sm:px-6">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4 text-center">8 quoting tools for UK builders: feature comparison</h2>
            <p className="text-lg text-muted-foreground mb-10 text-center max-w-2xl mx-auto">
              Six dimensions, eight tools, honest "best for" verdicts. Scores reflect the UK market specifically.
            </p>
            <div className="overflow-x-auto rounded-xl border border-border">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border bg-muted/40">
                    <th className="text-left px-4 py-4 font-semibold min-w-[150px]">Tool</th>
                    <th className="text-center px-3 py-4 font-semibold min-w-[90px]">Smart Quoting</th>
                    <th className="text-center px-3 py-4 font-semibold min-w-[100px]">UK cost library</th>
                    <th className="text-center px-3 py-4 font-semibold min-w-[110px]">Reads drawings</th>
                    <th className="text-center px-3 py-4 font-semibold min-w-[80px]">Mobile</th>
                     <th className="text-left px-3 py-4 font-semibold min-w-[100px]">Starting price</th>
                    <th className="text-left px-3 py-4 font-semibold min-w-[90px]">Free trial</th>
                    <th className="text-left px-3 py-4 font-semibold min-w-[220px]">Best for</th>
                  </tr>
                </thead>
                <tbody>
                  {TOOLS.map((tool, i) => (
                    <tr key={i} className="border-b border-border/60 last:border-0 hover:bg-muted/20">
                      <td className="px-4 py-4 font-semibold">
                        {tool.altHref ? (
                          <Link href={tool.altHref} className="text-primary hover:underline">
                            {tool.name}
                          </Link>
                        ) : (
                          tool.name
                        )}
                        {i === 0 && (
                          <span className="ml-2 text-xs font-medium text-primary bg-primary/10 px-2 py-0.5 rounded-full">This site</span>
                        )}
                      </td>
                      <td className="px-3 py-4 text-center"><TriCell value={tool.aiQuoting} /></td>
                      <td className="px-3 py-4 text-center"><TriCell value={tool.ukCostLibrary} /></td>
                      <td className="px-3 py-4 text-center"><TriCell value={tool.readsDrawings} /></td>
                      <td className="px-3 py-4 text-center"><TriCell value={tool.mobile} /></td>
                      <td className="px-3 py-4">{tool.pricePerMonth}</td>
                      <td className="px-3 py-4">{tool.freeTrial}</td>
                      <td className="px-3 py-4 text-muted-foreground text-xs">{tool.bestFor}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex items-center gap-6 mt-4 text-xs text-muted-foreground flex-wrap">
              <span className="flex items-center gap-1"><CheckCircle2 className="h-3.5 w-3.5 text-green-600" /> Yes / Strong</span>
              <span className="flex items-center gap-1"><Minus className="h-3.5 w-3.5 text-amber-600" /> Partial / Limited</span>
              <span className="flex items-center gap-1"><XCircle className="h-3.5 w-3.5 text-red-500" /> No</span>
               <span className="ml-auto">Pricing and features checked against publicly available data on 19 September 2026. Verify current details directly with each provider before purchasing.</span>
            </div>
          </div>
        </section>

        {/* Tool-by-tool breakdown */}
        <section className="py-16 sm:py-20 border-t border-border/40">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">Tool-by-tool breakdown</h2>
            <p className="text-lg text-muted-foreground mb-12">
              Strengths, limitations, and the honest answer for each tool: when to stay and when to switch.
            </p>
            <div className="space-y-10">
              {TOOL_DETAILS.map((tool) => (
                <div key={tool.name} className="border border-border rounded-xl p-6">
                  <div className="flex items-center gap-3 mb-5">
                    <h3 className="text-xl font-bold">{tool.name}</h3>
                    {tool.altHref && (
                      <Link href={tool.altHref} className="text-xs text-primary border border-primary/30 rounded-full px-2 py-0.5 hover:bg-primary/5 transition-colors flex-shrink-0">
                        See full comparison →
                      </Link>
                    )}
                  </div>
                  <div className="grid sm:grid-cols-2 gap-6 mb-5">
                    <div>
                      <p className="text-xs font-semibold text-green-600 dark:text-green-400 uppercase tracking-wide mb-3">Strengths</p>
                      <ul className="space-y-2">
                        {tool.strengths.map((s, i) => (
                          <li key={i} className="flex items-start gap-2 text-sm text-muted-foreground">
                            <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
                            {s}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-red-500 dark:text-red-400 uppercase tracking-wide mb-3">Limitations</p>
                      <ul className="space-y-2">
                        {tool.limitations.map((l, i) => (
                          <li key={i} className="flex items-start gap-2 text-sm text-muted-foreground">
                            <XCircle className="h-4 w-4 text-red-500 dark:text-red-400 flex-shrink-0 mt-0.5" />
                            {l}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                  <div className="bg-muted/40 rounded-lg px-4 py-3 text-sm">
                    <span className="font-semibold">Stay on {tool.name} if:</span>{" "}
                    <span className="text-muted-foreground">{tool.stayIf}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Per-competitor links */}
        <section className="py-16 sm:py-20 bg-muted/30 border-t border-border/40">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">See the full head-to-head comparisons</h2>
            <p className="text-lg text-muted-foreground mb-8">
              Each page goes deeper: detailed feature tables, pricing breakdowns, who should switch, and who should stay.
            </p>
            <div className="grid sm:grid-cols-2 gap-4">
              {[
                { href: "/tradify-alternative", name: "Tradify", desc: "The Proper Tool vs Tradify for UK builders" },
                { href: "/buildxact-alternative", name: "Buildxact", desc: "The Proper Tool vs Buildxact: UK market comparison" },
                { href: "/simpro-alternative", name: "simPRO", desc: "The Proper Tool vs simPRO for small builders" },
                { href: "/estimatorxpress-alternative", name: "EstimatorXpress", desc: "Cloud-based quoting vs EstimatorXpress desktop workflow" },
              ].map((link) => (
                <Link key={link.href} href={link.href}>
                  <div className="group border border-border rounded-xl p-5 hover:border-primary/50 hover:shadow-sm transition-all cursor-pointer flex items-center justify-between">
                    <div>
                      <p className="font-semibold group-hover:text-primary transition-colors">{link.name} Alternative</p>
                      <p className="text-sm text-muted-foreground mt-0.5">{link.desc}</p>
                    </div>
                    <ChevronRight className="h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors flex-shrink-0 ml-3" />
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16 sm:py-20 border-t border-border/40">
          <div className="max-w-3xl mx-auto px-4 sm:px-6">
            <h2 className="text-3xl sm:text-4xl font-bold mb-10 text-center">Frequently asked questions</h2>
            <div className="space-y-8">
              {FAQS.map((faq) => (
                <div key={faq.q} className="border-b border-border/60 pb-8 last:border-0 last:pb-0">
                  <h3 className="font-semibold text-lg mb-3">{faq.q}</h3>
                  <p className="text-muted-foreground leading-relaxed">{faq.a}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 sm:py-20 bg-primary/5 border-t border-border/40">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">Try The Proper Tool free for 14 days</h2>
            <p className="text-lg text-muted-foreground mb-8">
              Generate a professional, fully itemised construction quote from drawings or a description in under 10 minutes. No credit card required.
            </p>
            <Button size="lg" asChild>
              <Link href="/login">
                Start free, no credit card needed
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
            <p className="text-sm text-muted-foreground mt-4">14-day free trial · Plans from £29/mo · Cancel any time</p>
          </div>
        </section>

        {/* Disclaimer */}
        <section className="py-8 border-t border-border/40 bg-muted/20">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <p className="text-xs text-muted-foreground leading-relaxed">
               <strong className="text-foreground">Disclaimer:</strong> Competitor pricing and feature information on this page was checked against publicly available data on 19 September 2026. Prices and features may change. esti-mate information was checked against estimate.co.uk, including its Windows platform, optional UK libraries and assisted takeoff. We are the maker of The Proper Tool and have a commercial interest in recommending it; we have tried to give fair, measured assessments of all tools. Verify current details directly with each provider before making a purchasing decision.
            </p>
          </div>
        </section>
      </main>

      <footer className="border-t border-border/40 py-8">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-muted-foreground">
          <p>© {new Date().getFullYear()} The Proper Tool. Built for UK builders.</p>
          <div className="flex gap-6">
            <Link href="/privacy" className="hover:text-foreground transition-colors">Privacy</Link>
            <Link href="/terms" className="hover:text-foreground transition-colors">Terms</Link>
            <Link href="/blog" className="hover:text-foreground transition-colors">Blog &amp; Guides</Link>
            <Link href="/comparisons" className="hover:text-foreground transition-colors">Compare</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
